import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { useMemo, useState } from 'react'
import { Link } from 'react-router'

import { DataGrid, type BulkAction, type GridFilter } from '../DataGrid'
import { EnvToggle } from '../EnvToggle'

import { api, can, publicSiteUrl, type PageReport, type PageRow } from '../api'
import { ErrorBox, FindingsList, Pill } from '../components'
import { dt } from '../format'

// Public paths per page (the site's Swedish routes; English is under /en).
const sitePath: Record<string, string> = { 'showcase/domains': '/showcase/domaner' }

const pageFilters: GridFilter<PageRow>[] = [
  {
    key: 'prod',
    label: 'Prod',
    options: ['published', 'draft', 'archived'].map((v) => ({ value: v, label: v })),
    value: (p) => p.status,
  },
  {
    key: 'dev',
    label: 'Dev',
    options: [
      { value: 'on', label: 'published' },
      { value: 'off', label: 'not published' },
    ],
    value: (p) => (p.dev_published_version != null ? 'on' : 'off'),
  },
  { key: 'kind', label: 'Kind', options: ['showcase', 'product'].map((v) => ({ value: v, label: v })), value: (p) => p.kind },
  {
    key: 'gate',
    label: 'Gate',
    options: [
      { value: 'ok', label: 'can publish' },
      { value: 'blocked', label: 'blocked' },
    ],
    value: (p) => (p.gate.ok ? 'ok' : 'blocked'),
  },
]

export function PublicPages() {
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const q = useQuery({ queryKey: ['pages'], queryFn: api.pages })
  const [open, setOpen] = useState<string | null>(null)
  const purge = useMutation({ mutationFn: () => api.purgeCache({ all: true }) })
  const ready = useMutation({
    mutationFn: api.publishReady,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pages'] })
      qc.invalidateQueries({ queryKey: ['endpoints'] })
    },
  })
  const admin = can(me.data, 'admin')
  const actions = useMemo<BulkAction<PageRow>[]>(
    () =>
      admin
        ? [
            {
              label: 'Publish on dev',
              primary: true,
              applies: (p) => p.status !== 'archived' && p.dev_published_version !== p.current_version,
              run: (p) => api.publishPageDev(p.kind, p.slug),
            },
            { label: 'Unpublish from dev', applies: (p) => p.dev_published_version != null, run: (p) => api.unpublishPageDev(p.kind, p.slug) },
            {
              label: 'Publish on prod',
              primary: true,
              applies: (p) => p.status !== 'archived' && p.published_version !== p.current_version,
              run: (p) => api.publishPage(p.kind, p.slug),
            },
            {
              label: 'Unpublish from prod',
              applies: (p) => p.status === 'published',
              confirm: 'Take these pages off aleva.se?',
              run: (p) => api.unpublishPage(p.kind, p.slug),
            },
          ]
        : [],
    [admin],
  )
  const refresh = () => qc.invalidateQueries({ queryKey: ['pages'] })
  const cols = useMemo<ColDef<PageRow>[]>(
    () => [
      {
        field: 'title',
        headerName: 'Page',
        flex: 2,
        minWidth: 260,
        cellRenderer: ({ data: p }: ICellRendererParams<PageRow>) =>
          p ? (
            <span className="cell-two">
              <span>{p.title}</span>
              <a className="mono" href={`${publicSiteUrl}${sitePath[`${p.kind}/${p.slug}`] ?? p.path}`} target="_blank" rel="noreferrer">
                {p.path}
              </a>
            </span>
          ) : null,
      },
      { field: 'kind', width: 110 },
      {
        headerName: 'Dev',
        width: 150,
        sortable: false,
        cellRenderer: ({ data: p }: ICellRendererParams<PageRow>) =>
          p ? (
            <EnvToggle label="Dev" version={p.dev_published_version} current={p.current_version} canPublish={p.gate.ok} admin={admin}
              publish={() => api.publishPageDev(p.kind, p.slug)} unpublish={() => api.unpublishPageDev(p.kind, p.slug)} onDone={refresh} />
          ) : null,
      },
      {
        headerName: 'Prod',
        width: 150,
        sortable: false,
        cellRenderer: ({ data: p }: ICellRendererParams<PageRow>) =>
          p ? (
            p.status === 'archived' ? (
              <Pill kind="neutral">archived</Pill>
            ) : (
              <EnvToggle label="Prod" version={p.status === 'published' ? p.published_version : null} current={p.current_version}
                canPublish={p.gate.ok} admin={admin} publish={() => api.publishPage(p.kind, p.slug)}
                unpublish={() => api.unpublishPage(p.kind, p.slug)} onDone={refresh} />
            )
          ) : null,
      },
      {
        headerName: 'Gate (latest)',
        width: 150,
        cellRenderer: ({ data: p }: ICellRendererParams<PageRow>) =>
          p ? p.gate.ok ? <Pill kind="ok">can publish</Pill> : <Pill kind="err">{p.gate.findings.errors.length + p.gate.problems.length} blocking</Pill> : null,
      },
      {
        headerName: 'Endpoints shown',
        width: 150,
        cellClass: 'mono',
        valueGetter: (v) => (v.data ? `${v.data.gate.endpoints.filter((e) => e.published).length}/${v.data.gate.endpoints.length}` : ''),
      },
      { field: 'updated_at', headerName: 'Updated', width: 150, valueFormatter: (v) => dt(v.value) },
      {
        headerName: '',
        width: 100,
        sortable: false,
        cellRenderer: ({ data: p }: ICellRendererParams<PageRow>) => {
          if (!p) return null
          const id = `${p.kind}/${p.slug}`
          return (
            <button className="btn btn-sm" onClick={() => setOpen((o) => (o === id ? null : id))}>
              {open === id ? 'Close' : 'Open'}
            </button>
          )
        },
      },
    ],
    [open, admin], // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>Public pages</h1>
          <p className="sub">
            Showcases and products on aleva.se. The pages are code in public-site/; these records decide whether a page is live, its
            titles, and which endpoints it shows. Publishing (admin) checks that every endpoint it shows passes the licence gate;
            unpublished endpoints are left out of the page.
          </p>
        </div>
        {admin && (
          <div className="row">
            <button
              className="btn btn-primary"
              disabled={ready.isPending}
              onClick={() => ready.mutate()}
              title="Publish every draft endpoint, then every draft page, that passes the licence gate"
            >
              {ready.isPending ? 'Publishing…' : 'Publish everything that passes on prod'}
            </button>
            <button className="btn" disabled={purge.isPending} onClick={() => purge.mutate()} title="Drop every cached page and API response">
              {purge.isPending ? 'Purging…' : 'Purge cache'}
            </button>
            {purge.isSuccess && <Pill kind="ok">purged</Pill>}
          </div>
        )}
      </header>
      {purge.error && <ErrorBox error={purge.error} />}
      {ready.error && <ErrorBox error={ready.error} />}
      {ready.data && (
        <div className="callout">
          <p>
            Published {ready.data.published.length}: {ready.data.published.map((p) => `${p.kind} ${p.slug}`).join(', ') || 'none'}.
          </p>
          {ready.data.refused.length > 0 && (
            <details>
              <summary>Not published ({ready.data.refused.length})</summary>
              <ul>{ready.data.refused.map((p) => <li key={p.kind + p.slug}><b>{p.kind} {p.slug}</b>: {p.reason}</li>)}</ul>
            </details>
          )}
        </div>
      )}
      {q.error && <p className="callout err">{q.error.message}</p>}
      <DataGrid<PageRow>
        rows={q.data}
        columnDefs={cols}
        loading={q.isLoading}
        getRowId={(p) => `${p.kind}/${p.slug}`}
        searchText={(p) => [p.title, p.slug, p.kind, p.path].join(' ')}
        filters={pageFilters}
        actions={actions}
        onDone={refresh}
        onOpen={(p) => setOpen(`${p.kind}/${p.slug}`)}
      />
      {open && <PageDetail key={open} kind={open.split('/')[0]} slug={open.split('/')[1]} onChanged={() => qc.invalidateQueries({ queryKey: ['pages'] })} />}
    </section>
  )
}

function PageDetail({ kind, slug, onChanged }: { kind: string; slug: string; onChanged: () => void }) {
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const q = useQuery({ queryKey: ['page', kind, slug], queryFn: () => api.page(kind, slug) })
  const done = (d: PageRow) => {
    qc.setQueryData(['page', kind, slug], d)
    onChanged()
  }
  const publish = useMutation({ mutationFn: () => api.publishPage(kind, slug), onSuccess: done })
  const unpublish = useMutation({ mutationFn: () => api.unpublishPage(kind, slug), onSuccess: done })
  if (q.error) return <p className="callout err">{q.error.message}</p>
  if (!q.data) return <p className="muted">Loading…</p>
  const p = q.data
  const admin = can(me.data, 'admin')
  return (
    <div className="card">
      <div className="row-between">
        <h2>
          {p.title} <span className="muted mono">{p.kind}/{p.slug}</span>
        </h2>
        {admin && (
          <div className="row">
            <button className="btn btn-primary" disabled={!p.gate.ok || publish.isPending || p.published_version === p.current_version}
              onClick={() => publish.mutate()}>
              Publish v{p.current_version}
            </button>
            {p.status === 'published' && (
              <button className="btn" disabled={unpublish.isPending} onClick={() => unpublish.mutate()}>
                Unpublish
              </button>
            )}
          </div>
        )}
      </div>
      {publish.error && <ErrorBox error={publish.error} />}
      {unpublish.error && <ErrorBox error={unpublish.error} />}
      <div className="cols-2">
        <div>
          <h3 className="h3">Gate</h3>
          {p.gate.problems.map((x, i) => (
            <p key={i} className="callout err">{x}</p>
          ))}
          <FindingsList findings={p.gate.findings} empty="Every endpoint shown passes the licence gate." />
        </div>
        <div>
          <h3 className="h3">Endpoints</h3>
          <ul className="audit">
            {p.gate.endpoints.map((e) => (
              <li key={e.slug}>
                <Link to={`/endpoints/${e.slug}`} className="mono">{e.path}</Link>{' '}
                {e.published ? <Pill kind="ok">live</Pill> : <Pill kind="warn">not published</Pill>}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <PageSpecEditor p={p} editor={can(me.data, 'editor')} onSaved={done} />
    </div>
  )
}

function PageSpecEditor({ p, editor, onSaved }: { p: PageRow; editor: boolean; onSaved: (d: PageRow) => void }) {
  const current = p.versions?.[0]
  const [text, setText] = useState(() => JSON.stringify(current?.body ?? {}, null, 2))
  const [comment, setComment] = useState('')
  const [report, setReport] = useState<PageReport | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const parsed = (): unknown | undefined => {
    try {
      setParseError(null)
      return JSON.parse(text)
    } catch (e) {
      setParseError((e as Error).message)
      return undefined
    }
  }
  const validate = useMutation({ mutationFn: (s: unknown) => api.validatePage(p.kind, p.slug, s), onSuccess: setReport })
  const save = useMutation({ mutationFn: (s: unknown) => api.savePage(p.kind, p.slug, s, comment), onSuccess: onSaved })
  return (
    <>
      <h3 className="h3">Spec</h3>
      <p className="note">
        Versions: {p.versions?.map((v) => `v${v.version} (${v.created_by}, ${dt(v.created_at)})`).join(' · ')}. Saving creates v{p.current_version + 1};
        an admin publishes it.
      </p>
      <textarea className="spec mono" spellCheck={false} rows={16} value={text} readOnly={!editor}
        onChange={(e) => {
          setText(e.target.value)
          setReport(null)
        }} />
      {parseError && <p className="callout err">Not valid JSON: {parseError}</p>}
      <div className="row">
        <button className="btn" onClick={() => {
          const s = parsed()
          if (s !== undefined) validate.mutate(s)
        }}>
          Validate
        </button>
        {editor && (
          <>
            <input className="grow" placeholder="What changed" value={comment} onChange={(e) => setComment(e.target.value)} />
            <button className="btn btn-primary" disabled={save.isPending} onClick={() => {
              const s = parsed()
              if (s !== undefined) save.mutate(s)
            }}>
              Save as v{p.current_version + 1}
            </button>
          </>
        )}
      </div>
      {validate.error && <ErrorBox error={validate.error} />}
      {save.error && <ErrorBox error={save.error} />}
      {report && (
        <div className="report">
          {report.problems.map((x, i) => (
            <p key={i} className="callout err">{x}</p>
          ))}
          <FindingsList findings={report.findings} empty="Valid, and passes the gate." />
        </div>
      )}
    </>
  )
}
